import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Tag, Space,
    message, Card, Row, Col, Statistic, Rate, Divider, Alert, Select
} from 'antd';
import {
    SaveOutlined, SendOutlined, TrophyOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import AYSService from '../../services/AYSService';
import util from '../../utils/util';

const { TextArea } = require('antd').Input;

const JUDGE_CRITERIA = [
    { key: 'c1_clinical',       label: 'Clinical Excellence',                  marker: 'AACC, COP' },
    { key: 'c2_safety',         label: 'Patient Safety & Risk Management',     marker: 'IPCM, PSQI, FMS' },
    { key: 'c3_innovation',     label: 'Innovation & Improvement Initiatives', marker: 'PSQI' },
    { key: 'c4_efficiency',     label: 'Service Efficiency & Accessibility',   marker: 'AACC' },
    { key: 'c5_community',      label: 'Community Engagement & Impact',        marker: 'PRPE' },
    { key: 'c6_leadership',     label: 'Leadership & Staff Development',       marker: 'GROM, FMS, HRE' },
    { key: 'c7_documentation',  label: 'Documentation & Evidence',             marker: 'ISM' },
    { key: 'c8_alignment',      label: 'Alignment with Ministry Goals',        marker: 'AACC, COP' },
];

const SCORE_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Adequate', 4: 'Good', 5: 'Excellent' };

export default function QoCJudgePortal() {
    const [assessments, setAssessments]   = useState([]);
    const [loading, setLoading]           = useState(false);
    const [scoringModal, setScoringModal] = useState(false);
    const [current, setCurrent]           = useState(null);
    const [scores, setScores]             = useState({});
    const [comments, setComments]         = useState('');
    const [saving, setSaving]             = useState(false);
    const [periodFilter, setPeriodFilter] = useState('ENHEA-2026');
    const isAdmin = util.getModules()?.['ays_admin'] === 1 || parseInt(localStorage.getItem('is_admin') || '0') === 1 || parseInt(localStorage.getItem('is_national') || '0') === 1;
    const [adminView, setAdminView]       = useState(false);
    const [matrix, setMatrix]             = useState([]);
    const [matrixLoading, setMatrixLoading] = useState(false);
    const [periods, setPeriods]           = useState([{ label: 'ENHEA-2026' }]);

    useEffect(() => {
        loadAssessments();
        import('../../services/QoCService').then(({ default: QoCService }) => {
            QoCService.getPeriods().then(r => {
                const enhea = (r.data?.data || []).filter(p => p.label.startsWith('ENHEA'));
                if (enhea.length) setPeriods(enhea);
            });
        });
    }, [periodFilter]); // eslint-disable-line

    const loadMatrix = () => {
        setMatrixLoading(true);
        Promise.all([
            AYSService.getHaAllAssessments({ period_label: periodFilter }),
            AYSService.getJudgeScores({ period_label: periodFilter }),
        ]).then(([aRes, jRes]) => {
            const assessments = aRes.data?.data || [];
            const scores      = jRes.data?.data || [];

            // Group judge scores by assessment
            const scoreMap = {};
            scores.forEach(s => {
                if (!scoreMap[s.assessment_id]) scoreMap[s.assessment_id] = [];
                scoreMap[s.assessment_id].push(s);
            });

            // Build matrix
            const mat = assessments.map(a => {
                const aScores   = scoreMap[a.id] || [];
                const submitted = aScores.filter(s => s.status === 'submitted');
                const avgJudge  = submitted.length > 0
                    ? Math.round(submitted.reduce((sum, s) => sum + (parseFloat(s.total_score)||0), 0) / submitted.length / 40 * 100)
                    : null;
                const nqmp      = parseFloat(a.overall_percentage || 0);
                const final     = avgJudge !== null
                    ? (nqmp * 0.7 + avgJudge * 0.3).toFixed(1)
                    : nqmp.toFixed(1);

                return {
                    ...a,
                    judge_scores:    aScores,
                    submitted_count: submitted.length,
                    avg_judge_pct:   avgJudge,
                    final_score:     final,
                };
            });

            setMatrix(mat);
        }).catch(() => {})
        .finally(() => setMatrixLoading(false));
    };

    const loadAssessments = () => {
        setLoading(true);
        AYSService.getHaAllAssessments({ period_label: periodFilter })
            .then(r => setAssessments(r.data?.data || []))
            .catch(() => setAssessments([]))
            .finally(() => setLoading(false));
    };

    const openScoring = async (assessment) => {
        util.showLoader();
        try {
            const res = await AYSService.getJudgeScores({ assessment_id: assessment.id });
            const existing = (res.data?.data || [])[0];
            setCurrent(assessment);
            if (existing) {
                const s = {};
                JUDGE_CRITERIA.forEach(c => { s[c.key] = existing[c.key] ? parseInt(existing[c.key]) : null; });
                setScores(s);
                setComments(existing.comments || '');
            } else {
                setScores({});
                setComments('');
            }
            setScoringModal(true);
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const totalScore  = JUDGE_CRITERIA.reduce((sum, c) => sum + (scores[c.key] || 0), 0);
    const scoredCount = JUDGE_CRITERIA.filter(c => scores[c.key]).length;
    const totalPct    = Math.round((totalScore / 40) * 100);
    const nqmpPct     = parseFloat(current?.overall_percentage || 0);
    const finalEst    = (nqmpPct * 0.7 + totalPct * 0.3).toFixed(1);

    const saveScores = async (submit = false) => {
        if (submit && scoredCount < 8) { message.warning('Score all 8 criteria before submitting'); return; }
        setSaving(true);
        try {
            await AYSService.saveJudgeScores({
                assessment_id:  current.id,
                facility_name:  current.facility_name || current.program_name || '',
                award_category: current.category_name || '',
                comments,
                status: submit ? 'submitted' : 'draft',
                ...scores,
                total_score: totalScore,
            });
            message.success(submit ? 'Scores submitted' : 'Draft saved');
            if (submit) setScoringModal(false);
            loadAssessments();
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    const grouped = {};
    assessments.forEach(a => {
        const cat = a.category_name || 'Uncategorised';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(a);
    });

    const cols = [
        { title: 'Facility / Programme', dataIndex: 'facility_name', render: (t, r) => (
            <div>
                <div style={{ fontWeight: 700 }}>{t || r.program_name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{r.site_type} · {r.region}</div>
            </div>
        )},
        { title: 'NQMP Score', dataIndex: 'overall_percentage', width: 110,
            render: v => v ? <span style={{ fontWeight: 700 }}>{parseFloat(v).toFixed(1)}%</span> : <span style={{ color:'#ccc' }}>—</span>
        },
        { title: 'My Score', width: 120, render: (_, r) => (
            r.judge_scored
                ? <Tag color="green"><CheckCircleOutlined /> Scored</Tag>
                : <Tag color="orange">Pending</Tag>
        )},
        { title: 'Status', dataIndex: 'status', width: 110,
            render: v => <Tag color={v === 'approved' ? 'green' : 'blue'}>{v}</Tag>
        },
        { title: '', width: 110, render: (_, r) => (
            <Button size="small" type="primary" icon={<TrophyOutlined />} onClick={() => openScoring(r)}>
                {r.judge_scored ? 'Edit' : 'Score'}
            </Button>
        )},
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>Judge Portal</h4>
                    <small className="text-muted">Health Excellence Awards - Independent Evaluation</small>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {isAdmin && (
                        <div style={{ display:'flex', background:'rgba(255,255,255,0.1)', borderRadius:8, padding:2 }}>
                            <button onClick={() => setAdminView(false)}
                                style={{ padding:'4px 14px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                                    background: !adminView ? '#fff' : 'transparent', color: !adminView ? '#0d2137' : 'rgba(255,255,255,0.7)' }}>
                                My Scores
                            </button>
                            <button onClick={() => setAdminView(true)}
                                style={{ padding:'4px 14px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600,
                                    background: adminView ? '#fff' : 'transparent', color: adminView ? '#0d2137' : 'rgba(255,255,255,0.7)' }}>
                                All Judges
                            </button>
                        </div>
                    )}
                    <Select value={periodFilter} onChange={v => setPeriodFilter(v)} style={{ width: 160 }}>
                        {periods.map(p => <Select.Option key={p.label} value={p.label}>{p.label}</Select.Option>)}
                    </Select>
                </div>
            </div>

            <div className="page-pad">
                <Row gutter={12} style={{ marginBottom: 20 }}>
                    {[
                        { label: 'Total to Evaluate', value: assessments.length, color: '#1890ff' },
                        { label: 'Scored by Me', value: assessments.filter(a => a.judge_scored).length, color: '#16a34a' },
                        { label: 'Pending', value: assessments.filter(a => !a.judge_scored).length, color: '#d97706' },
                    ].map(k => (
                        <Col key={k.label} span={6}>
                            <Card size="small">
                                <Statistic title={k.label} value={k.value}
                                    valueStyle={{ color: k.color, fontWeight: 800, fontSize: 28 }} />
                            </Card>
                        </Col>
                    ))}
                    <Col span={6}>
                        <Card size="small" style={{ background: '#fffbeb', border: '1px solid #fbbf24' }}>
                            <div style={{ fontSize: 11, color: '#92400e' }}>Final Score Formula</div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>70% NQMP + 30% Judges</div>
                        </Card>
                    </Col>
                </Row>

                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message="Score each facility on 8 criteria (1=Poor to 5=Excellent). Total out of 40. Your scores are averaged with other judges and weighted at 30% of the final award score." />

                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e',
                            borderLeft: '3px solid #d97706', paddingLeft: 10, marginBottom: 8 }}>{cat}</div>
                        <Table rowKey="id" dataSource={items} columns={cols}
                            size="small" pagination={false} loading={loading} />
                    </div>
                ))}

                {assessments.length === 0 && !loading && !adminView && (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                        <div style={{ fontSize: 48 }}>⚖️</div>
                        <div style={{ fontWeight: 700, fontSize: 16, marginTop: 12 }}>No assessments to evaluate</div>
                        <div style={{ fontSize: 13 }}>Approved ENHEA assessments for {periodFilter} will appear here</div>
                    </div>
                )}

                {/* Admin Matrix View */}
                {adminView && isAdmin && (
                    <div>
                        <div style={{ marginBottom: 16, padding: '10px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e40af' }}>
                            Showing all judge scores for {periodFilter}. Final score = 70% NQMP + 30% judge average.
                        </div>
                        <Table rowKey="id" size="small" loading={matrixLoading}
                            dataSource={matrix}
                            pagination={false}
                            columns={[
                                { title: 'Facility / Programme', dataIndex: 'facility_name', render: (t, r) => (
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{t || r.program_name}</div>
                                        <div style={{ fontSize: 11, color: '#888' }}>{r.category_name}</div>
                                    </div>
                                )},
                                { title: 'NQMP %', dataIndex: 'overall_percentage', width: 90,
                                    render: v => <span style={{ fontWeight: 700 }}>{parseFloat(v||0).toFixed(1)}%</span>
                                },
                                { title: 'Judges Scored', width: 110, render: (_, r) => (
                                    <span style={{ color: r.submitted_count > 0 ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>
                                        {r.submitted_count} judge{r.submitted_count !== 1 ? 's' : ''}
                                    </span>
                                )},
                                { title: 'Judge Avg %', width: 100, render: (_, r) => (
                                    r.avg_judge_pct !== null
                                        ? <span style={{ fontWeight: 700, color: '#d97706' }}>{r.avg_judge_pct}%</span>
                                        : <span style={{ color: '#ccc' }}>Pending</span>
                                )},
                                { title: 'Final Score', width: 100, render: (_, r) => (
                                    <span style={{ fontWeight: 800, fontSize: 16, color: '#1e40af' }}>{r.final_score}%</span>
                                )},
                                { title: 'Judge Breakdown', render: (_, r) => (
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {r.judge_scores.map((s, i) => (
                                            <Tag key={i} color={s.status === 'submitted' ? 'green' : 'orange'}
                                                style={{ fontSize: 11 }}>
                                                {s.judge_name}: {s.total_score ? `${s.total_score}/40` : 'Draft'}
                                            </Tag>
                                        ))}
                                        {r.judge_scores.length === 0 && <span style={{ color: '#ccc', fontSize: 12 }}>No scores yet</span>}
                                    </div>
                                )},
                            ]} />
                    </div>
                )}
            </div>

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <TrophyOutlined style={{ color: '#d97706' }} />
                        <span>{current?.facility_name || current?.program_name}</span>
                        {scoredCount === 8 && <Tag color="gold">{totalScore}/40 ({totalPct}%)</Tag>}
                    </div>
                }
                open={scoringModal}
                onCancel={() => setScoringModal(false)}
                width={800}
                footer={
                    <Space>
                        <Button onClick={() => setScoringModal(false)}>Close</Button>
                        <Button icon={<SaveOutlined />} loading={saving} onClick={() => saveScores(false)}>Save Draft</Button>
                        <Button type="primary" icon={<SendOutlined />} loading={saving}
                            disabled={scoredCount < 8} onClick={() => saveScores(true)}>Submit Scores</Button>
                    </Space>
                }>

                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 16px', marginBottom: 12 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div style={{ fontSize: 11, color: '#888' }}>Facility / Programme</div>
                            <div style={{ fontWeight: 700 }}>{current?.facility_name || current?.program_name}</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ fontSize: 11, color: '#888' }}>NQMP Score</div>
                            <div style={{ fontWeight: 700, color: '#1890ff' }}>{nqmpPct.toFixed(1)}%</div>
                        </Col>
                        <Col span={6}>
                            <div style={{ fontSize: 11, color: '#888' }}>Category</div>
                            <div style={{ fontWeight: 700 }}>{current?.category_name || '—'}</div>
                        </Col>
                    </Row>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${(scoredCount / 8) * 100}%`, height: '100%',
                            background: scoredCount === 8 ? '#16a34a' : '#d97706', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                        {scoredCount}/8 scored - {totalScore}/40
                    </span>
                </div>

                {JUDGE_CRITERIA.map((criterion, i) => {
                    const val = scores[criterion.key] || 0;
                    return (
                        <Card key={criterion.key} size="small" style={{
                            marginBottom: 8,
                            borderLeft: val ? '3px solid #d97706' : '3px solid #e5e7eb',
                            background: val ? '#fffbeb' : '#fafafa',
                        }}>
                            <Row align="middle" gutter={8}>
                                <Col span={1}>
                                    <span style={{ fontWeight: 700, color: '#9ca3af', fontSize: 12 }}>{i + 1}</span>
                                </Col>
                                <Col span={11}>
                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{criterion.label}</div>
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Ref: {criterion.marker}</div>
                                </Col>
                                <Col span={9}>
                                    <Rate count={5} value={val}
                                        onChange={v => setScores(prev => ({ ...prev, [criterion.key]: v }))}
                                        style={{ fontSize: 22 }} />
                                    {val > 0 && <span style={{ fontSize: 11, color: '#d97706', marginLeft: 8, fontWeight: 600 }}>{SCORE_LABELS[val]}</span>}
                                </Col>
                                <Col span={3} style={{ textAlign: 'right' }}>
                                    {val > 0 && <span style={{ fontSize: 18, fontWeight: 800, color: '#d97706' }}>{val}/5</span>}
                                </Col>
                            </Row>
                        </Card>
                    );
                })}

                <Divider />

                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col span={12}>
                        <div style={{ fontSize: 12, color: '#888' }}>Total Judge Score</div>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#d97706' }}>
                            {totalScore}/40
                            <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>({totalPct}%)</span>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Est. Final Score (70% NQMP + 30% Judge)</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af' }}>{finalEst}%</div>
                    </Col>
                </Row>

                <div style={{ fontWeight: 600, marginBottom: 6 }}>Comments & Recommendations</div>
                <TextArea rows={3} placeholder="Optional comments for consensus review..."
                    value={comments} onChange={e => setComments(e.target.value)} />
            </Modal>
        </div>
    );
}
